import { useCallback, useEffect } from 'react';

import { env } from '@/env';
import { useAnalytics } from '@/hooks/useAnalytics';
import { RECAPTCHA_VALIDATION_FAILED } from '@/consts/events';

interface UseReCaptchaOptions {
  screen: string;
}

interface UseReCaptchaReturn {
  executeReCaptcha: (action: string) => Promise<string>;
  isReCaptchaEnabled: boolean;
}

type GrecaptchaEnterprise = {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

const SCRIPT_URL = 'https://www.google.com/recaptcha/enterprise.js?render=';
const LOAD_FAILED = 'reCAPTCHA Enterprise script failed to load';
const NOT_CONFIGURED = 'reCAPTCHA is not configured';

let enterprisePromise: Promise<GrecaptchaEnterprise> | null = null;

const readEnterprise = (): GrecaptchaEnterprise | undefined =>
  (window as unknown as { grecaptcha?: { enterprise?: GrecaptchaEnterprise } }).grecaptcha
    ?.enterprise;

const loadEnterprise = (siteKey: string): Promise<GrecaptchaEnterprise> => {
  if (enterprisePromise !== null) return enterprisePromise;

  enterprisePromise = new Promise<GrecaptchaEnterprise>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${SCRIPT_URL}${siteKey}`;
    script.async = true;
    script.addEventListener('load', () => {
      const enterprise = readEnterprise();
      if (enterprise === undefined) {
        reject(new Error(LOAD_FAILED));
        return;
      }
      enterprise.ready(() => resolve(enterprise));
    });
    script.addEventListener('error', () => reject(new Error(LOAD_FAILED)));
    document.head.appendChild(script);
  });

  return enterprisePromise;
};

/**
 * Hook for Google reCAPTCHA Enterprise integration.
 * Returns a function to execute reCAPTCHA and get a token.
 *
 * Usage:
 * ```
 * const { executeReCaptcha } = useReCaptcha({ screen: 'signup' });
 * const token = await executeReCaptcha('submit_form');
 * // Send token to backend for validation
 * ```
 */
export const useReCaptcha = ({ screen }: UseReCaptchaOptions): UseReCaptchaReturn => {
  const siteKey = env.RECAPTCHA_SITE_KEY;
  const { trackError } = useAnalytics();

  useEffect(() => {
    if (!siteKey) return;
    loadEnterprise(siteKey).catch(() => {});
  }, [siteKey]);

  const executeReCaptcha = useCallback(
    async (action: string): Promise<string> => {
      try {
        if (!siteKey) throw new Error(NOT_CONFIGURED);
        const enterprise = await loadEnterprise(siteKey);
        return await enterprise.execute(siteKey, { action });
      } catch (error) {
        trackError({
          event: RECAPTCHA_VALIDATION_FAILED,
          properties: {
            action,
            screen,
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        });
        throw error;
      }
    },
    [screen, siteKey, trackError]
  );

  return {
    executeReCaptcha,
    isReCaptchaEnabled: !!siteKey,
  };
};
